import React, { useEffect, useState } from "react";

type Indicator = "UP" | "HASISSUES" | "UNDERMAINTENANCE" | "unknown";

interface InstatusStatus {
  indicator: Indicator;
  description: string;
}

const STATUS_PAGE_URL = "https://myhustatus.instatus.com";
const SUMMARY_URL = `${STATUS_PAGE_URL}/summary.json`;

const dotColor: Record<Indicator, string> = {
  UP: "bg-emerald-500",
  HASISSUES: "bg-orange-500",
  UNDERMAINTENANCE: "bg-yellow-400",
  unknown: "bg-gray-400",
};

const pulseColor: Record<Indicator, string> = {
  UP: "bg-emerald-500",
  HASISSUES: "bg-orange-500",
  UNDERMAINTENANCE: "bg-yellow-400",
  unknown: "bg-gray-400",
};

export const StatusBadge: React.FC = () => {
  const [status, setStatus] = useState<InstatusStatus | null>(null);

  useEffect(() => {
    let cancelled = false;

    const descriptions: Record<Indicator, string> = {
      UP: "All systems operational",
      HASISSUES: "Partial outage",
      UNDERMAINTENANCE: "Under maintenance",
      unknown: "Status unavailable",
    };

    fetch(SUMMARY_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          const indicator: Indicator =
            (data?.page?.status as Indicator) ?? "unknown";
          setStatus({
            indicator,
            description: descriptions[indicator] ?? "Status unknown",
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus({
            indicator: "unknown",
            description: "Status unavailable",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!status) return null;

  const indicator = status.indicator in dotColor ? status.indicator : "unknown";

  return (
    <a
      href={STATUS_PAGE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
      aria-label={`System status: ${status.description}`}
    >
      <span className="relative flex h-2 w-2">
        {indicator === "UP" && (
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full ${pulseColor[indicator]} opacity-75`}
          />
        )}
        <span
          className={`relative inline-flex rounded-full h-2 w-2 ${dotColor[indicator]}`}
        />
      </span>
      <span className="text-xs text-muted-foreground">
        {status.description}
      </span>
    </a>
  );
};
